import {useEffect} from 'react';

import {safeLocalStorage} from '../util/localStorage';

export const WB_AGENT_CROSS_TAB_SYNC_KEY = 'wb-agent-chat-sync';
const MAX_EVENT_AGE_MS = 5 * 60 * 1000;

const tabId = crypto.randomUUID();

export type WBAgentCrossTabEvent =
  | {
      type: 'turn-cancel-requested';
      rootTurnId: string;
      turnId: string;
      timestamp: number;
      sourceTabId: string;
      nonce: string;
    }
  | {
      type: 'turn-created';
      rootTurnId: string;
      turnId?: string;
      timestamp: number;
      sourceTabId: string;
      nonce: string;
    }
  | {
      type: 'turn-updated';
      rootTurnId: string;
      turnId: string;
      timestamp: number;
      sourceTabId: string;
      nonce: string;
    }
  | {
      type: 'thread-renamed';
      rootTurnId: string;
      timestamp: number;
      sourceTabId: string;
      nonce: string;
    }
  | {
      type: 'thread-deleted';
      rootTurnId: string;
      timestamp: number;
      sourceTabId: string;
      nonce: string;
    };

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

type PublishableEvent = DistributiveOmit<
  WBAgentCrossTabEvent,
  'sourceTabId' | 'timestamp' | 'nonce'
>;

type CrossTabChannel = Pick<
  BroadcastChannel,
  'postMessage' | 'addEventListener' | 'removeEventListener' | 'close'
>;

type CrossTabChannelFactory = (name: string) => CrossTabChannel | null;

const createDefaultChannel: CrossTabChannelFactory = name => {
  if (typeof BroadcastChannel === 'undefined') {
    return null;
  }
  return new BroadcastChannel(name);
};

let createChannel = createDefaultChannel;
let publishChannel: CrossTabChannel | null | undefined;

export function publishWBAgentCrossTabEvent(event: PublishableEvent): void {
  const payload: WBAgentCrossTabEvent = {
    ...event,
    sourceTabId: tabId,
    timestamp: Date.now(),
    nonce: crypto.randomUUID(),
  };

  const channel = getPublishChannel();
  if (channel) {
    channel.postMessage(payload);
    return;
  }

  safeLocalStorage.setItem(
    WB_AGENT_CROSS_TAB_SYNC_KEY,
    JSON.stringify(payload)
  );
}

export function useWBAgentCrossTabEvents(
  onEvent: (event: WBAgentCrossTabEvent) => void
): void {
  useEffect(() => subscribeWBAgentCrossTabEvents(onEvent), [onEvent]);
}

export function subscribeWBAgentCrossTabEvents(
  onEvent: (event: WBAgentCrossTabEvent) => void
): () => void {
  const channel = createChannel(WB_AGENT_CROSS_TAB_SYNC_KEY);
  if (channel) {
    const handleMessage = (event: MessageEvent<unknown>) => {
      const parsed = parseWBAgentCrossTabEvent(event.data);
      if (parsed) {
        onEvent(parsed);
      }
    };
    channel.addEventListener('message', handleMessage);
    return () => {
      channel.removeEventListener('message', handleMessage);
      channel.close();
    };
  }

  const handleStorageChange = (event: StorageEvent) => {
    if (event.key !== WB_AGENT_CROSS_TAB_SYNC_KEY) {
      return;
    }
    const parsed = parseWBAgentCrossTabEvent(event.newValue);
    if (parsed) {
      onEvent(parsed);
    }
  };
  window.addEventListener('storage', handleStorageChange);
  return () => window.removeEventListener('storage', handleStorageChange);
}

export function parseWBAgentCrossTabEvent(
  value: unknown
): WBAgentCrossTabEvent | null {
  if (!value) {
    return null;
  }

  try {
    const parsed = (
      typeof value === 'string' ? JSON.parse(value) : value
    ) as Partial<WBAgentCrossTabEvent>;
    if (
      parsed.sourceTabId === tabId ||
      typeof parsed.sourceTabId !== 'string' ||
      typeof parsed.rootTurnId !== 'string' ||
      typeof parsed.timestamp !== 'number' ||
      typeof parsed.nonce !== 'string' ||
      Date.now() - parsed.timestamp > MAX_EVENT_AGE_MS
    ) {
      return null;
    }

    if (
      parsed.type !== 'turn-cancel-requested' &&
      parsed.type !== 'turn-created' &&
      parsed.type !== 'turn-updated' &&
      parsed.type !== 'thread-renamed' &&
      parsed.type !== 'thread-deleted'
    ) {
      return null;
    }
    if (
      (parsed.type === 'turn-cancel-requested' ||
        parsed.type === 'turn-updated') &&
      typeof parsed.turnId !== 'string'
    ) {
      return null;
    }

    return parsed as WBAgentCrossTabEvent;
  } catch {
    return null;
  }
}

function getPublishChannel(): CrossTabChannel | null {
  if (publishChannel === undefined) {
    publishChannel = createChannel(WB_AGENT_CROSS_TAB_SYNC_KEY);
  }
  return publishChannel;
}

export function __setWBAgentCrossTabChannelFactoryForTests(
  factory: CrossTabChannelFactory
): void {
  publishChannel?.close();
  publishChannel = undefined;
  createChannel = factory;
}

export function __resetWBAgentCrossTabSyncForTests(): void {
  publishChannel?.close();
  publishChannel = undefined;
  createChannel = createDefaultChannel;
}
